import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import Logger from '../utils/Logger';
import { getAttachmentImageDataCached, fetchAttachmentImageDataCached } from '../utils/cache/attachmentImageCache';
import { BUNDLE_DISTRIBUTED_EVENT, hasPermissionEntries } from '../utils/bundle/permissionsUtils';

const PermissionContext = createContext(null);

export const PermissionProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyPermissions = useCallback((next) => {
    if (hasPermissionEntries(next)) {
      setPermissions(next);
      Logger.log('Page permissions applied:', next);
      return true;
    }
    setPermissions({});
    Logger.warn('No usable permissions entries', next);
    return false;
  }, []);

  const loadPermissions = useCallback(async (forceRefresh = false) => {
    if (!isAuthenticated) {
      setPermissions({});
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const configData = await fetchAttachmentImageDataCached(forceRefresh);

      if (!applyPermissions(configData?.permissions)) {
        Logger.warn('No permissions data found after bundle fetch');
      }
    } catch (err) {
      setError(err);
      Logger.error('Error loading page permissions from bundle:', err);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, applyPermissions]);

  useEffect(() => {
    const initPermissions = async () => {
      if (!isAuthenticated) {
        setPermissions({});
        setLoading(false);
        setError(null);
        return;
      }

      const cachedData = getAttachmentImageDataCached();
      if (hasPermissionEntries(cachedData?.permissions)) {
        applyPermissions(cachedData.permissions);
        setLoading(false);
        return;
      }

      setLoading(true);
      await loadPermissions(false);

      // Bundle은 이미 로드됐는데 Context만 비어 있는 경우 한 번 더 동기 캐시 확인
      const again = getAttachmentImageDataCached();
      if (hasPermissionEntries(again?.permissions)) {
        applyPermissions(again.permissions);
      }
      setLoading(false);
    };

    initPermissions();
  }, [isAuthenticated, applyPermissions, loadPermissions]);

  // Bundle 분배 완료 후 권한 재적용 (세션 복원 레이스 방지)
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const onBundleDistributed = (event) => {
      const next = event?.detail?.permissions;
      if (applyPermissions(next)) {
        setLoading(false);
      } else {
        const cached = getAttachmentImageDataCached();
        if (applyPermissions(cached?.permissions)) {
          setLoading(false);
        }
      }
    };

    window.addEventListener(BUNDLE_DISTRIBUTED_EVENT, onBundleDistributed);
    return () => window.removeEventListener(BUNDLE_DISTRIBUTED_EVENT, onBundleDistributed);
  }, [isAuthenticated, applyPermissions]);

  const hasCategoryPermission = (category) => {
    return Object.prototype.hasOwnProperty.call(permissions, category);
  };

  const hasPagePermission = (category, page) => {
    const pages = permissions[category];
    if (!Array.isArray(pages)) {
      return false;
    }
    return pages.includes(page);
  };

  const refreshPermissions = () => loadPermissions(false);

  const forceRefreshPermissions = async () => {
    await loadPermissions(true);
  };

  const value = {
    permissions,
    loading,
    error,
    hasCategoryPermission,
    hasPagePermission,
    refreshPermissions,
    forceRefreshPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }
  return context;
};
